import { SEARCH_DEPLOYMENT_ID } from '@integration-layer/data/xGenIds'
import type { AlgoliaProductType } from '@integration-layer/modules/05.productServices/runtime/components/WrapperProductTile.props'
import { stripCloudinaryDimensionsFromProduct } from '@integration-layer/utils/stripCloudinaryDimensions'

/**
 * Parameters for performing a search query with Xgen.
 * @typedef {Object} SearchParams
 */
type SearchParams = {
  /** Search query string */
  q: string
  /** Brand filter */
  brand?: string
  /** Color filter (single or multiple) */
  color?: string | string[]
  /** Micro category filter */
  microCat?: string | string[]
  /** Macro category filter */
  macroCat?: string | string[]
  /** Gender filter */
  gender?: string | string[]
  /** Size filter */
  size?: string | string[]
  /** Age filter */
  age?: string | string[]
  /** Page number for pagination */
  page?: number
}

/**
 * Parameters for fetching recommendations from Xgen.
 * @typedef {Object} RecommendationParams
 */
type RecommendationParams = {
  /** Xgen container identifier (v3 containers API) */
  containerId: string
}

/**
 * Recommendation container returned by the Xgen containers API (v3).
 * The integration wrapper already formats `prediction` as Algolia-shaped products.
 * @typedef {Object} XgenContainer
 */
type XgenContainer = {
  /** Container identifier */
  id: string
  /** Container title configured in Xgen */
  title?: string
  /** Recommended products */
  prediction?: AlgoliaProductType[]
}

/**
 * Interface for Xgen integration SDK.
 * @interface XgenIntegration
 */
interface XgenIntegration {
  /**
   * Performs a search query with specified options.
   * @param {string} query - Search query string
   * @param {SearchOptions} options - Search configuration options
   * @returns {Promise<any>} Search results
   */
  search: (query: string, options: SearchOptions) => Promise<any>

  /**
   * Retrieves a single recommendation container (v3 containers API).
   * @param {{ id: string }} params - Container identifier
   * @returns {Promise<XgenContainer | undefined>} The container, if returned by Xgen
   */
  getContainerById: (params: {
    id: string
  }) => Promise<XgenContainer | undefined>

  /**
   * Sets a context attribute for search filtering.
   * @param {string} attribute - Context attribute name
   * @param {string} value - Context attribute value
   */
  setContext: (attribute: string, value: string) => void

  /** Clears all set context attributes */
  clearContext: () => void
}

/**
 * Configuration options for Xgen search.
 * @interface SearchOptions
 */
interface SearchOptions {
  /** Unique deployment identifier */
  deploymentId: string
  /** Page number for pagination */
  page: number
  /** Context attributes for filtering */
  context: { [key: string]: string | string[] }
  /** Whether to return facet information */
  facets: boolean
}

/**
 * Composable for interacting with Xgen AI-powered search and recommendation services.
 *
 * @description
 * Provides methods to perform AI-enhanced product searches and fetch recommendations.
 *
 * @returns {Object} An object with Xgen integration methods.
 */
/** Module-level shared state for the Xgen SDK (client-only) */
let xgenInstance: XgenIntegration | undefined
let xgenLoadPromise: Promise<void> | undefined

/**
 * Sends a diagnostic to Datadog Logs without ever throwing.
 *
 * Imported here, not at the top: a static import pulls the whole logging
 * package into the entry chunk for lines that run only on failure.
 *
 * Not awaited, and guarded on the resolved value as well as the promise:
 * throwing while reporting a failure must not replace the original error.
 *
 * @param {'info' | 'error'} level - Log level
 * @param {string} message - Stable event name
 * @param {Record<string, unknown>} context - Allowlisted, non-sensitive fields
 */
const reportToDatadog = (
  level: 'info' | 'error',
  message: string,
  context: Record<string, unknown>
) => {
  void import('@datadog/browser-logs')
    .then(logs => logs?.datadogLogs?.logger[level](message, context))
    .catch(() => undefined)
}

/**
 * Reduces an unknown thrown value to loggable metadata (no full error object).
 *
 * @param {unknown} error - Value thrown by the Xgen SDK (Error, HTTP status or API message)
 * @returns {{ errorName?: string, errorMessage: string }} Error metadata
 */
const toErrorMetadata = (error: unknown) =>
  error instanceof Error
    ? { errorName: error.name, errorMessage: error.message }
    : { errorMessage: String(error) }

export const useXgen = () => {
  /** Retrieve Xgen API key from runtime configuration */
  const API_KEY = useRuntimeConfig().public.xgen

  /**
   * Dynamically loads the Xgen integration script.
   * Shared across all composable instances — the SDK is loaded only once.
   */
  const checkScript = async () => {
    if (xgenInstance || !import.meta.client) return

    if (!xgenLoadPromise) {
      xgenLoadPromise = (async () => {
        const url = `https://assets.xgen.dev/js/${API_KEY}/sdkIntegration.js`
        try {
          const { integration } = await import(/* @vite-ignore */ url)
          xgenInstance = integration as XgenIntegration
        } catch (error) {
          reportToDatadog(
            'info',
            'Failed to load Xgen integration script',
            toErrorMetadata(error)
          )
        }
      })()
    }

    await xgenLoadPromise
  }

  /**
   * Performs an AI-enhanced product search with multiple filtering options.
   *
   * @param {SearchParams} searchParams - Search configuration parameters
   * @returns {Promise<any>} Search results with optional facets
   * @throws {Error} If Xgen integration fails to load
   * @description
   * - Supports multi-dimensional filtering (brand, color, category, etc.)
   * - Uses wildcard (*) for unspecified filters
   * - Includes pagination and facet information
   */
  const search = async ({
    q,
    brand,
    color,
    microCat,
    macroCat,
    gender,
    size,
    age,
    page,
  }: SearchParams) => {
    await checkScript()
    if (!xgenInstance) {
      console.warn('Xgen integration is not loaded, returning empty results.')
      return { products: [], facets: {} }
    }

    const { search } = xgenInstance

    const context = {
      Brand: brand ?? '*',
      ColorDesc: color?.length ? color : '*', // ['Blue', 'Black']
      macroCat: macroCat?.length ? macroCat : '*', // ['tshirt']
      microCat: microCat?.length ? microCat : '*',
      Gender: gender?.length ? gender : '*',
      size: size?.length ? size : '*', // ['XL']
      Age: age?.length ? age : '*',
    }

    return search(q, {
      deploymentId: SEARCH_DEPLOYMENT_ID,
      page: page ?? 0,
      context,
      facets: true,
    })
  }

  /**
   * Fetches product recommendations for an Xgen container (v3 containers API).
   *
   * @param {RecommendationParams} params - Recommendation request parameters
   * @returns {Promise<AlgoliaProductType[]>} Recommended products
   * @description
   * - Retrieves personalized product recommendations
   * - Returns products in Algolia-compatible format
   * - Resolves to an empty list when Xgen fails to load or respond
   */
  const recommendations = async ({
    containerId,
  }: RecommendationParams): Promise<AlgoliaProductType[]> => {
    await checkScript()
    if (!xgenInstance) {
      console.warn(
        'Xgen integration is not loaded, returning empty recommendations.'
      )
      return []
    }

    const { getContainerById } = xgenInstance

    try {
      const container = await getContainerById({ id: containerId })
      const products = container?.prediction
      if (!Array.isArray(products)) return []

      // Xgen catalog URLs often include fixed Cloudinary crops (w_*, h_*);
      // strip them so Nuxt Image can apply responsive sizing.
      return products.map(product =>
        stripCloudinaryDimensionsFromProduct(product)
      ) as AlgoliaProductType[]
    } catch (error) {
      reportToDatadog('error', 'xgen.recommendations.failed', {
        containerId,
        ...toErrorMetadata(error),
      })
      return []
    }
  }

  return {
    /** Checks and loads Xgen integration script */
    checkScript,
    /** Performs AI-enhanced product search */
    search,
    /** Fetches personalized product recommendations */
    recommendations,
  }
}
